<template>
  <DialogFrame
    v-model:visible="visible"
    :loading="loading"
    @onHide="onZoneCanceled"
    @onClose="closeModal"
    class="p-dialog-breathe"
  >
    <template #title>Edit Zone</template>

    <template #body>
      <div class="modal-body modal-body-add-zone">
        <DialogZoneForm
          v-if="stage === 1"
          v-model:newEvent="newEvent"
          v-model:zone="editZone"
          :index="index"
          @changeStage="goToStageTwo"
        />

        <DialogZoneSensors
          v-if="stage === 2"
          v-model:newEvent="newEvent"
          v-model:zone="editZone"
          :index="index"
          @changeStage="goToStageOne"
        />
      </div>
    </template>

    <template #footer>
      <Button
        v-if="stage === 1"
        :disabled="loading || !zoneValid"
        label="Save Changes"
        severity="secondary"
        @click="saveModal()"
        autofocus
      />

      <Button
        v-if="stage === 2"
        :disabled="loading"
        label="Done"
        severity="primary"
        @click="stage = 1"
        autofocus
      />
    </template>
  </DialogFrame>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

import type { SensorType } from '@/types/Sensor';
import type { EventTypeNew, ZoneType } from '@/types/Event';

import { Button } from 'primevue';

import DialogFrame from './DialogFrame.vue';
import DialogZoneForm from './forms/DialogZoneForm.vue';
import DialogZoneSensors from './forms/DialogZoneSensors.vue';

const newEvent = defineModel<EventTypeNew>('newEvent');
const editZone = defineModel<ZoneType>('editZone');

// Create an initial state we can refer back to if we cancel changes.
const zoneInitialState = <ZoneType>JSON.parse(JSON.stringify(editZone.value));

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    defaultSensors?: SensorType[];
    index: number;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits(['onZoneCanceled', 'onZoneEdited']);

const visible = ref(true);
const loading = ref(false);
const stage = ref(1);

const saveModal = () => {
  emit('onZoneEdited', editZone.value);
  stage.value = 1;
  visible.value = false;
};

const hasUniqueName = computed(() => {
  let nameUnique = true;

  // Trick it into ignoring itself when making the comparison
  if (newEvent.value?.zones) {
    const zones = [...newEvent.value.zones];
    zones.splice(props.index, 1);
    zones.forEach((zone) => {
      if (zone.name.toLowerCase().trim() === editZone.value?.name.toLowerCase().trim()) {
        nameUnique = false;
      }
    });
  }

  return nameUnique;
});

const zoneValid = computed(() => {
  if (editZone.value) {
    const hasName = editZone.value?.name?.trim() !== '';
    const hasType = editZone.value.categories.length > 0;
    const hasSensor = editZone.value.sensors.length > 0;
    let sensorsValid = true;

    editZone.value.sensors.forEach((sensor) => {
      if (!sensor.engaged_signal || !sensor.passerby_signal) {
        sensorsValid = false;
      }
    });

    return hasName && hasType && hasSensor && hasUniqueName.value && sensorsValid;
  }
  return false;
});

const onZoneCanceled = () => {
  if (newEvent.value?.zones) {
    newEvent.value.zones.splice(props.index, 1, zoneInitialState);
  }
  stage.value = 1;
  emit('onZoneCanceled');
};

const closeModal = () => {
  onZoneCanceled();
};

const goToStageTwo = () => {
  stage.value = 2;
};

const goToStageOne = () => {
  stage.value = 1;
};
</script>
